import { ReactElement, useContext } from 'react';
import * as turf from '@turf/turf';
import { FormattedMessage } from 'react-intl';
import {
  StyledCurrentLocation,
  StyledSpinner,
  StyledLoader,
  StyledLocationArrow,
} from './CurrentLocation.styles';
import AppContext from '../../../utils/createContext';
import type { AirportType } from '../../../utils/types';

type CurrentLocationProps = {
  onLocationChange: (airport: AirportType, disabled: boolean) => void;
  isDisabled: (airport: AirportType) => boolean;
};

export function CurrentLocation({
  onLocationChange,
  isDisabled,
}: CurrentLocationProps): ReactElement {
  const { airportsData, setUserLocation, setUserLocationError } = useContext(AppContext);
  let searching = false;
  const handleCurrentLocation = (geo: GeolocationPosition) => {
    if (geo) {
      searching = false;
      const currentLocation = turf.point([geo.coords.longitude, geo.coords.latitude]);
      const points = airportsData.map((airport: AirportType) =>
        turf.point([parseFloat(airport.longitude), parseFloat(airport.latitude)], {
          code: airport.iata_code,
        }),
      );
      const nearestPoint = turf.nearest(currentLocation, turf.featureCollection(points));
      const [nearestAirport] = airportsData.filter(
        (airport: AirportType) => airport.iata_code === nearestPoint.properties.code,
      );
      onLocationChange(nearestAirport, isDisabled(nearestAirport));
    }
  };
  const getCurrentPosition = () => {
    searching = true;
    navigator.geolocation.getCurrentPosition(
      (geolocation) => {
        setUserLocationError(false);
        setUserLocation(geolocation);
        handleCurrentLocation(geolocation);
      },
      (error) => error && setUserLocationError(true),
    );
  };

  return (
    <>
      {!searching ? (
        <StyledCurrentLocation onClick={getCurrentPosition}>
          <StyledLocationArrow />
          <FormattedMessage
            id="navigation-current-location-button"
            description="Current location button copy"
            defaultMessage="Current Location"
          />
        </StyledCurrentLocation>
      ) : (
        <StyledLoader>
          <StyledSpinner />
          <FormattedMessage
            id="navigation-current-location-loading"
            description="Loading message"
            defaultMessage="Loading..."
          />
        </StyledLoader>
      )}
    </>
  );
}
